import {
  Activity,
  LineChartUp02,
  ShieldZap,
  SlashCircle01,
  Target04,
  User01,
  VideoRecorder,
  XClose,
} from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { useBlockCreator } from "@/api/blocklist";
import { useCreator } from "@/api/creator";
import { GroupedActionMenu } from "@/components/action-menu/grouped-action-menu";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { BadgeWithIcon } from "@/components/base/badges/badges";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { CreatorBlockedBadge } from "@/components/blocklist/creator-blocked-badge";
import { UnblockCreatorModal } from "@/components/blocklist/unblock-creator-modal";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import { RemoveCreatorFromCollabModal } from "@/components/remove-creator-from-collab-modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useCurrency } from "@/hooks/use-currency";
import { useIsAssurance } from "@/hooks/use-is-assurance";
import { buildCreatorActionGroups } from "@/lib/creator-action-groups";
import { BulkCohortModal } from "@/pages/creator-cohorts/bulk-cohort-modal";
import { AddtoListsModal } from "@/pages/creator-lists/add-to-lists-modal";
import { error as toastError, success as toastSuccess } from "@/providers/ToastProvider";
import { getCreatorName } from "@/utils/creator-name";
import { ClassificationCard } from "./classification-card";
import { AboutTab } from "./tabs/about-tab";
import { ActivityTab } from "./tabs/activity-tab";
import { PerformanceTab } from "./tabs/performance-tab";
import { SafetyTab } from "./tabs/safety-tab";
import { VideosTab } from "./tabs/videos-tab";

// Tab definitions with icons
const tabs = [
  { id: "about" as const, label: "About", icon: <User01 className="w-4 h-4" /> },
  { id: "performance" as const, label: "Performance", icon: <LineChartUp02 className="w-4 h-4" /> },
  { id: "videos" as const, label: "Videos", icon: <VideoRecorder className="w-4 h-4" /> },
  { id: "safety" as const, label: "Safety", icon: <ShieldZap className="w-4 h-4" /> },
  { id: "activity" as const, label: "Activity", icon: <Activity className="w-4 h-4" /> },
];

const assuranceTabs = [
  { id: "about" as const, label: "About", icon: <User01 className="w-4 h-4" /> },
  { id: "performance" as const, label: "Performance", icon: <LineChartUp02 className="w-4 h-4" /> },
  { id: "videos" as const, label: "Videos", icon: <VideoRecorder className="w-4 h-4" /> },
  { id: "safety" as const, label: "Safety", icon: <ShieldZap className="w-4 h-4" /> },
  { id: "activity" as const, label: "Activity", icon: <Activity className="w-4 h-4" /> },
];

const numFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  compactDisplay: "short",
  notation: "compact",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const Inner = ({ id, onClose }: { id?: string; onClose: () => void }) => {
  const { data, isPending } = useCreator(id);
  const isAssurance = useIsAssurance();
  const [openDialog, setOpenDialog] = useState<"send-message">();
  const [selectedTab, setSelectedTab] = useState<"about" | "performance" | "videos" | "safety" | "activity">("about");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRemoveCollabModal, setShowRemoveCollabModal] = useState(false);
  const [addToListsOpen, setAddToListsOpen] = useState(false);
  const [addToCohortOpen, setAddToCohortOpen] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const blockCreator = useBlockCreator();
  const containerRef = useRef<HTMLDivElement>(null);

  const currency = useCurrency();
  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: "compact",
    compactDisplay: "short",
  });

  const activeTabsList = isAssurance ? assuranceTabs : tabs;

  // Scroll to top when tab changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [selectedTab]);

  if (isPending)
    return (
      <div className="flex w-full h-full items-center justify-center">
        <LoadingIndicator />
      </div>
    );

  if (!data)
    return (
      <div className="flex w-full h-full flex-col items-center justify-center gap-4 px-12">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-12 right-12 flex items-center justify-center p-2 rounded-lg cursor-pointer text-fg-quaternary hover:text-fg-quaternary_hover border border-transparent hover:border-border-primary active:border-border-primary transition duration-100 ease-linear"
          aria-label="Close"
        >
          <XClose className="size-5" />
        </button>
        <p className="text-base font-semibold text-primary">Creator profile not available</p>
        <p className="text-sm text-tertiary text-center">
          This creator's profile hasn't been synced yet. Try again later.
        </p>
      </div>
    );

  // Get collaboration count and names for the badge (active memberships only)
  const collaborations = (data?.targetCollaborations ?? []).filter(
    (c) => (c.kind === "target" || c.kind === "open") && c.membershipStatus !== "removed",
  );
  const collaborationCount = collaborations.length;
  const collaborationNames = collaborations.map((c) => c.name).filter(Boolean);

  // Get creator bio/title
  const creatorTitle = data?.bioDescription;
  const { nickname, handle } = getCreatorName(data?.nickname, data?.creatorName);
  const displayName = nickname ?? handle;

  return (
    <div ref={containerRef} className="w-full h-full flex flex-col overflow-y-auto px-[48px] py-[48px]">
      {/* Header Section */}
      <div className="space-y-6">
        {/* Profile Header */}
        <div className="flex gap-8 items-center">
          {data.thumbnail && (
            <img
              src={data.thumbnail}
              alt={displayName ?? "Creator"}
              className="w-[140px] h-[140px] rounded-full object-cover flex-shrink-0"
            />
          )}
          <div className="flex flex-col gap-5 grow min-w-0">
            <div className="flex flex-col gap-2">
              {/* Name with Collaboration count badge */}
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-2xl font-semibold text-primary">{displayName}</h2>
                {data.isBlocked && <CreatorBlockedBadge block={data.block ?? { appliedBy: null }} />}
                {/* Collaboration count badge with tooltip */}
                {collaborationCount > 0 && (
                  <Tooltip
                    title={
                      <div className="flex flex-col gap-1">
                        {collaborationNames.map((name) => (
                          <span key={name}>{name}</span>
                        ))}
                      </div>
                    }
                    placement="bottom"
                  >
                    <TooltipTrigger>
                      <BadgeWithIcon color="success" size="sm" iconLeading={Target04}>
                        {collaborationCount} {collaborationCount === 1 ? "collaboration" : "collaborations"}
                      </BadgeWithIcon>
                    </TooltipTrigger>
                  </Tooltip>
                )}
              </div>
              {nickname && handle && <p className="text-sm text-tertiary">{handle}</p>}
              {/* Creator bio/title */}
              <p className="text-sm text-tertiary italic">{creatorTitle || "No bio available"}</p>
            </div>
            {/* Performance Metrics - REAL DATA */}
            <div className="flex gap-6 items-center">
              <div className="flex flex-col">
                <span className="text-base font-medium text-primary">{currencyFormatter.format(data.avgGMV)}</span>
                <span className="text-sm text-tertiary">Avg GMV/ Video</span>
              </div>
              <div className="self-stretch w-0 border-l border-border-primary" />
              <div className="flex flex-col">
                <span className="text-base font-medium text-primary">{numFormatter.format(data.avgVideos)} videos</span>
                <span className="text-sm text-tertiary">Avg/ Month</span>
              </div>
              <div className="self-stretch w-0 border-l border-border-primary" />
              <div className="flex flex-col">
                <span className="text-base font-medium text-primary">{percentFormatter.format(data.avgCTR)}</span>
                <span className="text-sm text-tertiary">CTR</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Dropdown & Close Button */}
        <div className="absolute top-12 right-12 flex items-center gap-2">
          {id && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Dropdown.DotsButton className="flex items-center justify-center p-2 rounded-lg border border-transparent hover:border-border-primary active:border-border-primary data-[state=open]:border-border-primary" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-72 overflow-auto">
                {data.isBlocked ? (
                  <DropdownMenuItem onClick={() => setShowUnblockModal(true)}>
                    <SlashCircle01 className="mr-2 size-4" />
                    Unblock Creator
                  </DropdownMenuItem>
                ) : (
                  <GroupedActionMenu
                    groups={buildCreatorActionGroups({
                      onInvite: () => setShowInviteModal(true),
                      onMessage: () => setOpenDialog("send-message"),
                      onAddToList: () => setAddToListsOpen(true),
                      onAddToCohort: () => setAddToCohortOpen(true),
                      onBlock: async () => {
                        try {
                          await blockCreator.mutateAsync(data.tiktokCreatorOpenId ?? "");
                          toastSuccess("1 Creator blocked. Unblock anytime from Operations/Blocked Creators.");
                        } catch {
                          toastError("Failed to block creator");
                        }
                      },
                      onRemove: () => setShowRemoveCollabModal(true),
                      cohortsEnabled: true,
                      blocklistEnabled: !!data.tiktokCreatorOpenId,
                    })}
                  />
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center p-2 rounded-lg cursor-pointer text-fg-quaternary hover:text-fg-quaternary_hover border border-transparent hover:border-border-primary active:border-border-primary transition duration-100 ease-linear"
            aria-label="Close"
          >
            <XClose className="size-5" />
          </button>
        </div>

        {/* Classification Card */}
        {data.classification && (
          <ClassificationCard
            classification={data.classification}
            topCategory={data.categories?.[0]}
            avgVideos={data.avgVideos}
            avgGMV={data.avgGMV}
            videosWithTier1={data.safetyAnalysis.videosWithTier1}
            percentageTier2AtRisk={data.safetyAnalysis.percentageTier2AtRisk}
            currencyFormatter={currencyFormatter}
          />
        )}

        {/* Tabs Navigation - Full width */}
        <Tabs
          selectedKey={selectedTab}
          onSelectionChange={(key) => setSelectedTab(key as typeof selectedTab)}
          className="w-full border-b border-border-primary mt-15"
        >
          <Tabs.List type="underline" items={activeTabsList} className="w-full before:hidden">
            {(tab) => <Tabs.Item {...tab} className="flex-1" />}
          </Tabs.List>
        </Tabs>
      </div>

      {/* Tab Content */}
      <div className="mt-14">
        {/* About Tab */}
        {selectedTab === "about" && <AboutTab data={data} network={data.network} />}

        {/* Performance Tab */}
        {selectedTab === "performance" && (
          <PerformanceTab
            creatorId={data.creatorId}
            creatorName={displayName ?? data.creatorName}
            data={data}
            currencyFormatter={currencyFormatter}
          />
        )}

        {/* Videos Tab */}
        {selectedTab === "videos" && <VideosTab data={data} currencyFormatter={currencyFormatter} />}

        {/* Safety Tab */}
        {selectedTab === "safety" && isAssurance && <SafetyTab data={data} currencyFormatter={currencyFormatter} />}

        {/* Activity Tab */}
        {selectedTab === "activity" && (
          <div className="max-h-[800px]">
            <ActivityTab
              data={{
                creatorId: data.creatorId,
                creatorName: data.creatorName,
                creatorDisplayName: nickname ?? data.creatorName,
                creatorAvatar: data.thumbnail,
              }}
            />
          </div>
        )}
      </div>

      {/* Message Dialog */}
      {data && (
        <MessageCreatorsDialog
          recipients={[
            {
              creatorId: data.creatorId,
              handle: data.creatorName ?? null,
              thumbnail: data.thumbnail,
            },
          ]}
          open={openDialog === "send-message"}
          onOpenChange={(open) => setOpenDialog(open ? "send-message" : undefined)}
        />
      )}

      {/* Invite to Collaboration Modal — only renderable if we have the creator's TikTok open id;
          without it the invite API can't address them. */}
      {data?.tiktokCreatorOpenId && (
        <InviteToCollaborationModal
          creator={{
            openId: data.tiktokCreatorOpenId,
            username: data.creatorName,
            nickname: data.nickname,
            avatar: data.thumbnail,
          }}
          open={showInviteModal}
          onOpenChange={setShowInviteModal}
        />
      )}

      {/* Add to Lists Dialog */}
      {id && (
        <AddtoListsModal
          selectedCreators={[id]}
          onAddToList={() => setAddToListsOpen(false)}
          open={addToListsOpen}
          onOpenChange={setAddToListsOpen}
        />
      )}

      {id && (
        <BulkCohortModal
          creatorIds={[id]}
          open={addToCohortOpen}
          onOpenChange={setAddToCohortOpen}
          onApplied={() => setAddToCohortOpen(false)}
        />
      )}

      {data?.tiktokCreatorOpenId && showUnblockModal && (
        <UnblockCreatorModal
          creatorOpenId={data.tiktokCreatorOpenId}
          creatorName={displayName ?? "Creator"}
          open={showUnblockModal}
          onOpenChange={setShowUnblockModal}
        />
      )}

      {/* Remove from Collaboration Modal */}
      {data && (
        <RemoveCreatorFromCollabModal
          creator={data}
          open={showRemoveCollabModal}
          onOpenChange={setShowRemoveCollabModal}
        />
      )}
    </div>
  );
};

export const CreatorDetails = ({ id, onClose }: { id?: string; onClose: () => void }) => {
  const contentRef = useRef<HTMLDivElement>(null);

  if (!id) return null;

  return (
    <Sheet open={!!id} onOpenChange={onClose}>
      <SheetContent
        ref={contentRef}
        className="w-[900px] p-0 sm:max-w-[900px] flex flex-col"
        hideCloseButton
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          contentRef.current?.focus();
        }}
      >
        <Inner id={id} onClose={onClose} />
      </SheetContent>
    </Sheet>
  );
};
